
import { createContext, useCallback, useContext } from "react";
import Loading from "../../components/Loading";
import { toast, ToastContainer, TypeOptions, Id as ToastId } from "react-toastify";
import 'react-toastify/dist/ReactToastify.min.css';
import useState from "../../hooks/useState";
import { ToastTypes } from "../../data/Types";


interface INotificationContext {
    showLoading: () => void;
    hideLoading: () => void;

    showToast: (type: ToastTypes, message: string, timeout?: number) => void;
    updateToast: (id: number, type: TypeOptions, message: string, timeout?: number) => void;
    dismissToast: (id?: ToastId) => void;
}

interface IStateValues {
    loading: boolean;
}


const NotificationContext = createContext<INotificationContext | undefined>(undefined);

const NotificationContextProvider = (props: React.PropsWithChildren) => {
    const state = useState<IStateValues>({
        loading: false
    });


    const showToast = useCallback((type: ToastTypes, message: string, timeout: number = 5000) => {
        return toast[type](message, {
            autoClose: timeout
        });
    }, []);


    const updateToast = useCallback((id: number, type: TypeOptions, message: string, timeout: number = 5000) => {
        toast.update(id, {
            render: message, 
            type, 
            autoClose: timeout,
            isLoading: false
        });
    }, []);


    const dismissToast = useCallback((id?: ToastId) => {
        toast.dismiss(id);
    }, []);



    const data: INotificationContext = {
        showLoading: () => state.update('loading', true), 
        hideLoading: () => state.update('loading', false),
        showToast,
        updateToast,
        dismissToast
    }

    return (
        <NotificationContext.Provider value={data}>
            {props.children}

            <ToastContainer
                position="bottom-right"
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss={true}
                draggable={true}
                pauseOnHover={true}
                limit={5}
                theme='light'
            />

            <Loading open={state.values.loading} />
        </NotificationContext.Provider>
    );
}

const useNotificationContext = () => {
    const context = useContext(NotificationContext);
    
    if (context === undefined) {
        throw new Error('useNotificationContext must be used within a NotificationContextProvider');
    }
    
    return context;
}


export {
    useNotificationContext
}

export default NotificationContextProvider;